import { useState, useEffect, useRef } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Modal,
  CircularProgress,
  IconButton,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Lock, Email, Visibility, VisibilityOff } from "@mui/icons-material";
import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../context/ThemeContext";
import { Tooltip } from "@mui/material";
import Brightness4Icon from "@mui/icons-material/Brightness4"; // Moon
import Brightness7Icon from "@mui/icons-material/Brightness7"; // Sun

const Login = () => {
  const { mode, toggleTheme } = useThemeContext();
  const [emailForLogin, setEmailForLogin] = useState("");
  const [password, setPassword] = useState("");
  const { t } = useTranslation();
  const [errors, setErrors] = useState("");
  const { _setUser, setToken } = useStateContext();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Use session storage to persist errors through rerenders/refreshes
  const errorRef = useRef(sessionStorage.getItem('loginError') || "");

  // Keep the ref and state in sync
  useEffect(() => {
    if (errors) {
      sessionStorage.setItem('loginError', errors);
      errorRef.current = errors;
    }
  }, [errors]);

  // Load any saved error when component mounts
  useEffect(() => {
    const savedError = sessionStorage.getItem('loginError');
    if (savedError) {
      setErrors(savedError);
    }
  }, []);

  const [formErrors, setFormErrors] = useState({});

  const handleSignIn = (ev) => {
    ev.preventDefault();

    // Form validation
    const newErrors = {};
    if (!emailForLogin) newErrors.email = "Email is required";
    if (!password) newErrors.password = "Password is required";

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    setFormErrors({});
    // Use emailForLogin instead of email
    const email = emailForLogin.trim();
    const payload = { email, password };
    setLoading(true);

    axiosClient
      .post("auth/token/obtain/", payload)
      .then((data) => {
        setToken(data.data.access);
        _setUser(data.data.user);
        // Success! Clear any previous errors
        clearErrors(); // Use our clearErrors function that also clears sessionStorage
      })
      .catch((err) => {
        setLoading(false);
        const response = err.response;
        if (response) {
          setErrors(response.data.detail);
        } else {
          setErrors("Something went wrong! Try again later!");
        }
      });
  };

  // Reset password functionality
  const [open, setOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleEmailChange = (e) => {
    setResetEmail(e.target.value);
    // Clear error message when typing starts
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleRequestResetLink = async () => {
    setSuccessMessage("");
    setErrorMessage("");
    if (!resetEmail) {
      setErrorMessage("Email is required");
      return;
    }
    try {
      const payload = { email: resetEmail };
      const response = await axiosClient.post("/auth/forgot_password/", payload);
      if (response.status === 200) {
        setSuccessMessage("Reset link sent! Check your email.");
        setResetEmail("");
      }
    } catch (error) {
      if (error.response) {
        setErrorMessage(error.response.data?.email || "Unexpected error.");
      } else {
        setErrorMessage("Network error. Try again later.");
      }
    }
  };

  // Handle field changes without automatically clearing main error message
  const handleFieldChange = (field, value) => {
    if (field === 'emailForLogin') {
      setEmailForLogin(value);
      setFormErrors(prev => ({ ...prev, email: "" }));
    } else if (field === 'password') {
      setPassword(value);
      setFormErrors(prev => ({ ...prev, password: "" }));
    }
    // Don't clear the main errors state here anymore
  };

  // Clear all form fields and errors
  const clearForm = () => {
    setEmailForLogin("");
    setPassword("");
    setFormErrors({});
    clearErrors();
  };

  // Function to explicitly clear errors
  const clearErrors = () => {
    setErrors("");
    sessionStorage.removeItem('loginError');
    errorRef.current = "";
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ marginTop: 10 }}>
        <Card elevation={24} sx={{ borderRadius: 4, p: 3, position: "relative" }}>
          {/* Theme Toggler placed inside Card at top right */}
          <Box sx={{ position: "absolute", top: 16, right: 16 }}>
            <Tooltip title={`Switch to ${mode === "light" ? "Dark" : "Light"} Mode`}>
              <IconButton onClick={toggleTheme} color="inherit">
                {mode === "light" ? <Brightness4Icon /> : <Brightness7Icon />}
              </IconButton>
            </Tooltip>
          </Box>
          <CardContent>
            {/* <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
      
             Welcome to 
             Tempus Management System
            </Typography>
            <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
              { {t("Login")} }
            </Typography> */}

              <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                mr: 2,
                textAlign: "center",
                fontFamily: "TempusFont, Arial, sans-serif",
                fontSize: "32px",
                fontWeight: 400,
                letterSpacing: "1px",
                whiteSpace: "pre-line",
              }}
                >
                  Welcome to
                  <br />
                  Tempus Management System
                </Typography> 
 
 



            {/* Display API errors persistently - use errorRef for even more stability */}
            {(errors || errorRef.current) && (
              <Alert
                severity="error"
                sx={{ mt: 2, mb: 1 }}
                onClose={clearErrors} // Allow user to explicitly dismiss error
              >
                {errors || errorRef.current}
              </Alert>
            )}

            <Box component="form" noValidate sx={{ mt: 2 }} onSubmit={handleSignIn}>
              <TextField
                margin="normal"
                required
                fullWidth
                variant="outlined"
                id="emailForLogin"
                label={t("Email")}
                name="email"
                autoComplete="email"
                autoFocus
                value={emailForLogin}
                onChange={(e) => handleFieldChange('emailForLogin', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label={t("Password")}
                type={showPassword ? "text" : "password"}
                variant="outlined"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => handleFieldChange('password', e.target.value)}
                error={!!formErrors.password}
                helperText={formErrors.password}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* Add a clear errors button when there are API errors */}
              {errors && (
                <Box sx={{ mt: 1, textAlign: 'right' }}>
                  <Button
                    color="primary"
                    size="small"
                    onClick={clearErrors}
                  >
                    Try Again
                  </Button>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 50,
                    fontWeight: "bold",
                    fontSize: "1rem",
                    boxShadow: 3,
                    textTransform: "none",
                    flexGrow: 1,
                    mr: 1
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : t("Sign In")}
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={clearForm}
                  sx={{
                    height: 50,
                    fontWeight: "bold",
                    fontSize: "1rem",
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>

            <Typography
              onClick={handleOpen}
              variant="body2"
              align="center"
              component="p"
              gutterBottom
              sx={{
                mt: 2,
                cursor: "pointer",
                color: "primary.main",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Forgot password?
            </Typography>

            <Typography
              variant="body2"
              align="center"
              component="p"
              sx={{ mt: 2, color: "text.secondary" }}
            >
              Need Help? Contact on support@ti-talostech.com
            </Typography>

            {/* Modal for Forgot Password */}
            <Modal open={open} onClose={handleClose}>
              <Box
                sx={{
                  width: 400,
                  bgcolor: "background.paper",
                  borderRadius: 3,
                  boxShadow: 24,
                  p: 4,
                  mx: "auto",
                  mt: "10%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Reset Your Password
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Enter your email to receive a password reset link.
                </Typography>

                <TextField
                  label="Email Address"
                  variant="outlined"
                  value={resetEmail}
                  onChange={handleEmailChange}
                  fullWidth
                  margin="normal"
                  error={!!errorMessage}
                  helperText={errorMessage}
                />

                {successMessage && (
                  <Typography variant="body2" color="success.main" gutterBottom>
                    {successMessage}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button variant="contained" onClick={handleRequestResetLink} fullWidth>
                    Send Reset Link
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setResetEmail("");
                      setErrorMessage("");
                    }}
                    fullWidth
                  >
                    Clear
                  </Button>
                </Box>
                <Button onClick={handleClose} sx={{ mt: 2 }} fullWidth color="secondary">
                  Cancel
                </Button>
              </Box>
            </Modal>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;